import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export interface PdfExportOptions {
  filename: string;
  documentTitle?: string;
  isUnlocked?: boolean;
}

/**
 * Client-side Vector & High-DPI PDF Generator
 * Renders executive paperwork into an authentic, downloadable .pdf binary document.
 */
export async function exportElementToPdf(
  element: HTMLElement | null,
  options: PdfExportOptions
): Promise<void> {
  if (!element) {
    throw new Error('Target element for PDF export not found.');
  }

  // 1. Clone element so we can re-style without mutating the live UI
  const clone = element.cloneNode(true) as HTMLElement;
  clone.classList.add('pdf-export-mode');

  // Remove screen-only and non-document elements
  const nonPrintElements = clone.querySelectorAll('.screen-only, .no-print');
  nonPrintElements.forEach((el) => el.remove());

  // 2. Mount in off-screen container with calibrated Letter dimensions (816px = 8.5in at 96 DPI)
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  container.style.width = '816px';
  container.style.background = '#ffffff';
  container.style.zIndex = '-9999';
  container.appendChild(clone);
  document.body.appendChild(container);

  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'letter',
      compress: true,
    });

    const pageWidth = 612; // 8.5 in * 72 pt
    const pageHeight = 792; // 11 in * 72 pt
    const margin = 32; // ~0.44 in margin
    const contentWidth = pageWidth - margin * 2; // 548 pt
    const contentHeight = pageHeight - margin * 2; // 728 pt

    // Check if the document has explicit page blocks (e.g. AgreementArmor)
    const pageElements = clone.querySelectorAll('.pdf-page');

    if (pageElements.length > 0) {
      // Multi-page export with deterministic page breaks
      for (let i = 0; i < pageElements.length; i++) {
        const pageEl = pageElements[i] as HTMLElement;
        const canvas = await html2canvas(pageEl, {
          scale: 2, // 192 DPI
          backgroundColor: '#ffffff',
          useCORS: true,
          logging: false,
          windowWidth: 816,
        });

        if (i > 0) {
          pdf.addPage('letter', 'portrait');
        }

        const imgHeight = (canvas.height * contentWidth) / canvas.width;
        const renderHeight = Math.min(imgHeight, contentHeight);
        const imgData = canvas.toDataURL('image/jpeg', 0.98);

        pdf.addImage(imgData, 'JPEG', margin, margin, contentWidth, renderHeight);

        // Running institutional footer
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184); // #94a3b8
        pdf.text(
          `${options.documentTitle || 'Executive Deliverable'} · Page ${i + 1} of ${pageElements.length}`,
          margin,
          pageHeight - 16
        );
        pdf.text(
          options.isUnlocked ? 'CONFIDENTIAL & PROPRIETARY' : 'PRO UNLOCKS WATERMARK-FREE PDF',
          pageWidth - margin,
          pageHeight - 16,
          { align: 'right' }
        );
      }
    } else {
      // Single-page or continuous document export
      const canvas = await html2canvas(clone, {
        scale: 2, // 192 DPI
        backgroundColor: '#ffffff',
        useCORS: true,
        logging: false,
        windowWidth: 816,
      });

      const imgHeight = (canvas.height * contentWidth) / canvas.width;
      const imgData = canvas.toDataURL('image/jpeg', 0.98);

      if (imgHeight <= contentHeight) {
        // Fits entirely on a single page
        pdf.addImage(imgData, 'JPEG', margin, margin, contentWidth, imgHeight);

        // Running footer
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184);
        pdf.text(
          `${options.documentTitle || 'Executive Deliverable'} · Page 1 of 1`,
          margin,
          pageHeight - 16
        );
        pdf.text(
          options.isUnlocked ? 'CONFIDENTIAL & PROPRIETARY' : 'PRO UNLOCKS WATERMARK-FREE PDF',
          pageWidth - margin,
          pageHeight - 16,
          { align: 'right' }
        );
      } else if (imgHeight <= contentHeight * 1.15) {
        // Slightly exceeds page height: scale down slightly to preserve single-page unity
        const scaleFactor = contentHeight / imgHeight;
        const scaledWidth = contentWidth * scaleFactor;
        const offsetX = margin + (contentWidth - scaledWidth) / 2;
        pdf.addImage(imgData, 'JPEG', offsetX, margin, scaledWidth, contentHeight);

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184);
        pdf.text(
          `${options.documentTitle || 'Executive Deliverable'} · Page 1 of 1`,
          margin,
          pageHeight - 16
        );
        pdf.text(
          options.isUnlocked ? 'CONFIDENTIAL & PROPRIETARY' : 'PRO UNLOCKS WATERMARK-FREE PDF',
          pageWidth - margin,
          pageHeight - 16,
          { align: 'right' }
        );
      } else {
        // Multi-page slice for longer continuous documents
        let heightLeft = imgHeight;
        let position = 0;
        let pageNum = 1;

        while (heightLeft > 0) {
          if (pageNum > 1) {
            pdf.addPage('letter', 'portrait');
          }
          pdf.addImage(imgData, 'JPEG', margin, margin - position, contentWidth, imgHeight);

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.setTextColor(148, 163, 184);
          pdf.text(
            `${options.documentTitle || 'Executive Deliverable'} · Page ${pageNum}`,
            margin,
            pageHeight - 16
          );
          pdf.text(
            options.isUnlocked ? 'CONFIDENTIAL & PROPRIETARY' : 'PRO UNLOCKS WATERMARK-FREE PDF',
            pageWidth - margin,
            pageHeight - 16,
            { align: 'right' }
          );

          position += contentHeight;
          heightLeft -= contentHeight;
          pageNum++;
        }
      }
    }

    // Trigger instant client-side binary download
    pdf.save(options.filename);
  } finally {
    // 3. Clean up off-screen DOM container
    if (document.body.contains(container)) {
      document.body.removeChild(container);
    }
  }
}

